/**
 * ESAL (Equivalent Single Axle Load) Thickness Lookup Service
 *
 * Provides minimum thickness requirements based on ESAL values
 * according to AASHTO 1993 guidelines.
 *
 * Replicates Excel VLOOKUP logic from DATABASE.txt for:
 * - Asphalt Concrete minimum thickness (rows 7-12)
 * - Aggregate Base minimum thickness (rows 16-21)
 */

import esalDatabase from '../data/esal-thickness-tables.json';

export interface EsalThicknessRange {
    esalMin: number;
    esalMax: number | null; // null means no upper limit
    minThickness_m: number;
    minThickness_inches: number;
    note?: string;
}

export interface ThicknessLookupResult {
    minThickness_m: number;
    minThickness_inches: number;
    esalRange: { min: number; max: number | null };
    note?: string;
}

/**
 * ESAL Thickness Service
 * Implements Excel VLOOKUP logic for minimum thickness determination
 */
export class EsalThicknessService {
    /**
     * Get minimum asphalt concrete thickness based on ESAL
     * Replicates Excel formula: =VLOOKUP(0,I7:J12,2,)
     *
     * @param esal ESAL value (traffic load)
     * @returns Minimum thickness result
     */
    getAsphaltConcreteMinThickness(esal: number): ThicknessLookupResult {
        return this.lookupThickness(esal, esalDatabase.asphaltConcrete.ranges as EsalThicknessRange[]);
    }

    /**
     * Get minimum aggregate base thickness based on ESAL
     * Replicates Excel formula: =VLOOKUP(0,I16:J21,2,)
     *
     * @param esal ESAL value (traffic load)
     * @returns Minimum thickness result
     */
    getAggregateBaseMinThickness(esal: number): ThicknessLookupResult {
        return this.lookupThickness(esal, esalDatabase.aggregateBase.ranges as EsalThicknessRange[]);
    }

    /**
     * VLOOKUP logic: Find the appropriate range for a given ESAL value
     * Uses approximate match (finds largest value <= lookup value)
     *
     * @param esal ESAL value to lookup
     * @param ranges Array of thickness ranges
     * @returns Thickness lookup result
     */
    private lookupThickness(esal: number, ranges: EsalThicknessRange[]): ThicknessLookupResult {
        // Find the appropriate range
        // Excel VLOOKUP with approximate match finds the largest value <= lookup value
        let matchedRange: EsalThicknessRange | null = null;

        for (const range of ranges) {
            // Check if ESAL falls in this range
            if (esal >= range.esalMin && (range.esalMax === null || esal <= range.esalMax)) {
                matchedRange = range;
                break;
            }
        }

        // If no match found (shouldn't happen), use first range
        if (!matchedRange) {
            matchedRange = ranges[0];
        }

        return {
            minThickness_m: matchedRange.minThickness_m,
            minThickness_inches: matchedRange.minThickness_inches,
            esalRange: { min: matchedRange.esalMin, max: matchedRange.esalMax },
            note: matchedRange.note,
        };
    }

    /**
     * Get all asphalt concrete thickness ranges
     * @returns Array of ESAL ranges with thicknesses
     */
    getAsphaltConcreteRanges(): EsalThicknessRange[] {
        return esalDatabase.asphaltConcrete.ranges as EsalThicknessRange[];
    }

    /**
     * Get all aggregate base thickness ranges
     * @returns Array of ESAL ranges with thicknesses
     */
    getAggregateBaseRanges(): EsalThicknessRange[] {
        return esalDatabase.aggregateBase.ranges as EsalThicknessRange[];
    }

    /**
     * Determine traffic category based on ESAL
     * @param esal ESAL value
     * @returns Traffic category string
     */
    getTrafficCategory(esal: number): string {
        if (esal <= 50000) return 'Low traffic';
        if (esal <= 150000) return 'Light traffic';
        if (esal <= 500000) return 'Medium traffic';
        if (esal <= 2000000) return 'Heavy traffic';
        if (esal <= 7000000) return 'Very heavy traffic';
        return 'Extreme traffic';
    }

    /**
     * Get recommended reliability level based on functional class and ESAL
     * @param functionalClass Road functional classification
     * @param isUrban Urban or rural area
     * @returns Recommended reliability percentage string
     */
    getRecommendedReliability(
        functionalClass: 'interstate_freeway' | 'principal_arterial' | 'collector' | 'local',
        isUrban: boolean = true
    ): string {
        const classification = esalDatabase.reliabilityLevels.functionalClasses[functionalClass];
        return isUrban ? classification.urbanReliability : classification.ruralReliability;
    }

    /**
     * Validate if thickness meets minimum requirements
     * @param layerType 'asphalt' or 'base'
     * @param esal ESAL value
     * @param proposedThickness_m Proposed thickness in meters
     * @returns Object with validation result and minimum required
     */
    validateThickness(
        layerType: 'asphalt' | 'base',
        esal: number,
        proposedThickness_m: number
    ): { isValid: boolean; minRequired_m: number; difference_m: number } {
        const result =
            layerType === 'asphalt' ? this.getAsphaltConcreteMinThickness(esal) : this.getAggregateBaseMinThickness(esal);

        return {
            isValid: proposedThickness_m >= result.minThickness_m,
            minRequired_m: result.minThickness_m,
            difference_m: proposedThickness_m - result.minThickness_m,
        };
    }

    /**
     * Convert thickness between units
     * @param value Thickness value
     * @param fromUnit Source unit
     * @param toUnit Target unit
     * @returns Converted value
     */
    convertThickness(value: number, fromUnit: 'meters' | 'inches', toUnit: 'meters' | 'inches'): number {
        if (fromUnit === toUnit) return value;

        if (fromUnit === 'inches' && toUnit === 'meters') {
            return value * 0.0254;
        } else {
            return value / 0.0254;
        }
    }
}

/**
 * Singleton instance of ESAL Thickness Service
 */
export const esalThicknessService = new EsalThicknessService();
